import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Login from "./components/auth-flow/Login.jsx";
import SignUp from "./components/auth-flow/Signup.jsx";
import ForgotPassword from "./components/auth-flow/ForgotPassword.jsx";
import ResetPassword from "./components/auth-flow/ResetPassWord.jsx";
import VerifyCode from "./components/auth-flow/Verification.jsx";
import PaymentSuccess from "./components/PaymentSuccess.jsx";

import MakePayment from "./components/auth-flow/Payment.jsx";
import DashboardLayout from "./components/dashboard-flow/DashboardLayout.jsx";
import ClientDashboard from "./components/dashboard-flow/ClientDashboard.jsx";
import ProtectedRoutes from "./routes/ProtectedRoutes.jsx";
import RoleGate from "./routes/RoleGate.jsx";
import { readCurrentUser } from "./shared/_helpers/currentUser";
import { AuthProvider } from "./context/AuthContext.jsx";
import RouteErrorBoundary from "./routes/RouteErrorBoundary.jsx";
import Profile from "./components/UserProfile.jsx";
import Documents from "./components/UserDocuments.jsx";
import MeetingList from "./components/UserMeetings.jsx";
import MeetingDetails from "./components/UserMeetingDetails.jsx";
import UserList from "./components/UserList.jsx";
import AdminAdvisorsList from "./components/AdminAdvisorsList.jsx";
import AdvisorMeeting from "./components/AdvisorMeeting";
import AdvisorManageAvailability from "./components/AdvisorManageAvailability";
import AdminDashboard from "./components/dashboard-flow/AdminDashboard.jsx";
import Zoom from "./components/Zoom.jsx";
import AdminList from "./components/AdminList.jsx";
import PackageManagement from "./components/PackageManagement.jsx";
import ThreadPage from "./components/thread/ThreadPage.jsx";
import InboxPage from "./components/inbox/InboxPage.jsx";
import OnboardingGate from "./components/onboarding/OnboardingGate.jsx";
import AcknowledgePage from "./components/onboarding/AcknowledgePage.jsx";
import AgreementPage from "./components/onboarding/AgreementPage.jsx";
import ConsentsPage from "./components/onboarding/ConsentsPage.jsx";
import ReceiptPage from "./components/onboarding/ReceiptPage.jsx";
import YoureInPage from "./components/intake/YoureInPage.jsx";
import IntakeRoute from "./components/intake/IntakeRoute.jsx";
import ClientHome from "./components/intake/ClientHome.jsx";
import QuestionsPage from "./components/intake/QuestionsPage.jsx";
import PlanRoutes from "./components/plan/PlanRoutes.jsx";

function App() {
  return (
    <Router>
      <AuthProvider>
      <RouteErrorBoundary>
      <Routes>
        {/* The meeting add-on payment. RoleGate, not ProtectedRoutes: it
            must stay reachable without a web session (mobile-login path). */}
        <Route path="/make-payment" element={<RoleGate allowedRoles={["client"]}><MakePayment /></RoleGate>} />
        <Route path="/login" element={<Login />} />
        {/* Pooled inbox SP3: the pre-sign-in welcome — no guard. */}
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-code" element={<VerifyCode />} />
        <Route path="/confirm-payment" element={<RoleGate allowedRoles={["client"]}><PaymentSuccess /></RoleGate>} />
        {/* Pooled inbox SP5: the plan screens, client only (PlanRoutes
            holds /plan, /plan/payment, /plan/started and /plan/card). */}
        <Route
          path="/plan/*"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <PlanRoutes />
            </ProtectedRoutes>
          }
        />
        {/* Onboarding Routes — client-only (SEC-073). Pooled inbox SP4:
            /welcome is You're in, gated on trial_start like the documents
            pages (ahead → redirect, behind → read-only). */}
        <Route
          path="/welcome"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <OnboardingGate stageKey="trial_start">
                <YoureInPage />
              </OnboardingGate>
            </ProtectedRoutes>
          }
        />
        {/* Pooled inbox SP3 documents pages: client only, outside
            DashboardLayout (like /welcome); OnboardingGate reads the stage
            and redirects ahead / renders read-only behind. */}
        <Route
          path="/onboarding/acknowledge"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <OnboardingGate stageKey="documents_acknowledge">
                <AcknowledgePage />
              </OnboardingGate>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/onboarding/agreement"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <OnboardingGate stageKey="documents_agreement">
                <AgreementPage />
              </OnboardingGate>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/onboarding/consents"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <OnboardingGate stageKey="documents_consents">
                <ConsentsPage />
              </OnboardingGate>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/onboarding/done"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <OnboardingGate stageKey="documents_receipt">
                <ReceiptPage />
              </OnboardingGate>
            </ProtectedRoutes>
          }
        />
        {/* Pooled inbox SP4: the seven question pages, client only, outside
            DashboardLayout; the gate sends a client ahead of intake to their
            own page, and IntakeRoute sends one past it to /questionnaire. */}
        <Route
          path="/intake/:pageKey"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <OnboardingGate stageKey="intake">
                <IntakeRoute />
              </OnboardingGate>
            </ProtectedRoutes>
          }
        />
        {/* Dashboard Routes */}
        {/* /dashboard/:clientId is how staff open a client's detail view;
            /dashboard (no param) is the client's own. Different owners — keep
            them gated separately. */}
        <Route
          path="/dashboard/:clientId"
          element={
            <ProtectedRoutes allowedRoles={["advisor", "admin", "super-admin"]}>
              <DashboardLayout>
                <ClientDashboard />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        {/* Pooled inbox SP4: the client's dashboard by stage (decision 17) —
            the pass and answer cards at intake, the full layout after. */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <DashboardLayout>
                <ClientHome />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/admin-dashboard"
          element={
            <ProtectedRoutes allowedRoles={["admin", "super-admin"]}>
              <DashboardLayout>
                <AdminDashboard />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/connect/:id"
          element={
            <ProtectedRoutes>
              <Zoom />
            </ProtectedRoutes>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoutes allowedRoles={["advisor", "admin", "super-admin"]}>
              <DashboardLayout>
                <UserList />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/advisors"
          element={
            <ProtectedRoutes allowedRoles={["admin", "super-admin"]}>
              <DashboardLayout>
                <AdminAdvisorsList />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/admins"
          element={
            <ProtectedRoutes allowedRoles={["super-admin"]}>
              <DashboardLayout>
                <AdminList />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/meeting-details"
          element={
            <ProtectedRoutes>
              <DashboardLayout>
                <MeetingDetails />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        {/* User Routes */}
        <Route
          path="/profile"
          element={
            <ProtectedRoutes>
              <DashboardLayout>
                <Profile />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <DashboardLayout>
                <Documents />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/thread"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <DashboardLayout>
                <ThreadPage />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/advisor-manage-availability"
          element={
            <ProtectedRoutes allowedRoles={["advisor"]}>
              <DashboardLayout>
                <AdvisorManageAvailability />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/documents/:clientId"
          element={
            <ProtectedRoutes allowedRoles={["advisor", "admin", "super-admin"]}>
              <DashboardLayout>
                <Documents />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/meetings"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <DashboardLayout>
                <MeetingList />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/meetings/:clientId"
          element={
            <ProtectedRoutes allowedRoles={["advisor", "admin", "super-admin"]}>
              <DashboardLayout>
                <MeetingList />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/advisor-meetings"
          element={
            <ProtectedRoutes allowedRoles={["advisor"]}>
              <DashboardLayout>
                <AdvisorMeeting />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        {/* Pooled inbox SP2: advisor only. The web cannot tell a
            compliance-flagged admin from any other admin, so compliance
            readers are served by the API alone until SP7. */}
        <Route
          path="/inbox"
          element={
            <ProtectedRoutes allowedRoles={["advisor"]}>
              <DashboardLayout>
                <InboxPage />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/inbox/:threadId"
          element={
            <ProtectedRoutes allowedRoles={["advisor"]}>
              <DashboardLayout>
                <InboxPage />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/questionnaire"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <DashboardLayout>
                {/* SP4: answers edited in place; a client before intake
                    is sent to their own page (trial_start → You're in). */}
                <OnboardingGate stageKey="intake">
                  <QuestionsPage />
                </OnboardingGate>
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
         <Route
          path="/package-management"
          element={
            <ProtectedRoutes allowedRoles={["super-admin"]}>
              <DashboardLayout>
                <PackageManagement />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        {/* readCurrentUser, not a bare JSON.parse: this expression runs on
            every render of the route table, so a malformed stored value would
            white-screen every URL, not just this one. */}
        <Route
          path="/*"
          element={
            <Navigate
              to={readCurrentUser() ? "/dashboard" : "/login"}
            />
          }
        />
      </Routes>
      </RouteErrorBoundary>
      </AuthProvider>
    </Router >
  );
}

export default App;
